import Layout from "../components/Layout"
import Stack from "@mui/material/Stack"
import { Paper, Box, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material"
import { useQsFilters } from "../hooks/filters"
import { User } from "../types"
import { DataGrid, GridRowsProp, GridColDef } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import { useMemo } from "react"
import Paginator from "../components/Paginator"
import CustomToolbar from "../components/CustomToolbar"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import DialogForm from "../components/DialogForm"
import { Check, Close, Error, ManageAccounts } from "@mui/icons-material"
import { useCreateUtente, useDeleteUtente, useUpdateUtente, useUtentiList } from "../hooks/utenti"
import UtenteForm from "../components/forms/UtenteForm"
import { PageTitle } from "../components/PageTitle"
import { useSnackbarMessage } from "../hooks/snackbar"
import { useTranslation } from "react-i18next"
import { fetchApi } from "@inmagik/react-crud"
import { useAuth } from "../auth"
import { API_URL } from "../constants"
import DialogConfirm from "../components/DialogConfirm"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import FormatDate from "../components/FormatDate"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  ruolo: params.get("ruolo") ?? "",
})

export default function Utenti() {
  const pageSize = usePageSize()
  const { t } = useTranslation()
  const { filters, setFilters, uiFilters, setFiltersDebounced } = useQsFilters(initFilters)

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const [reinviaEmailModalState, reinviaEmailModalActions] = useModalTrigger()
  const [sbloccaModalState, sbloccaModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()
  const { tokens } = useAuth()

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: "full_name",
        headerName: t("user.full_name"),
        flex: 1,
      },
      { field: "email", headerName: t("user.email"), flex: 1 },
      {
        field: "last_login",
        display: "flex",
        headerName: t("user.is_active"),
        flex: 1,
        renderCell: (params) =>
          params.value ? (
            <Check color="success" />
          ) : params.row.is_locked ? (
            <Box display="flex" alignItems="center">
              <Error color="error" />
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => {
                  sbloccaModalActions.open(params.row as User)
                }}
              >
                {t("user.user_locked")}
              </Button>
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <Close color="error" />
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => {
                  reinviaEmailModalActions.open(params.row as User)
                }}
              >
                {t("user.cta_reinvia_email")}
              </Button>
            </Box>
          ),
      },
      {
        field: "ruolo",
        headerName: t("user.ruolo"),
        flex: 1,
        renderCell: (params) => <Box sx={{ textTransform: "capitalize" }}>{t(`user.enum_ruolo.${params.value}`)}</Box>,
      },
      {
        field: "scadenza",
        headerName: t("user.scadenza"),
        flex: 1,
        renderCell: (params) => <Box sx={{ textTransform: "capitalize" }}>{params.value ? <FormatDate>{params.value}</FormatDate> : null}</Box>,
      },

      {
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <IconButtonEdit onClick={() => editModalActions.open(params.row as User)} />
            <IconButtonDelete onClick={() => deleteModalActions.open(params.row as User)} />
          </Box>
        ),
      },
    ]
  }, [t, editModalActions, deleteModalActions])

  const { data, isLoading, refetch } = useUtentiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    ruolo: filters.ruolo,
  })

  const { mutateAsync: create } = useCreateUtente()
  const { mutateAsync: update } = useUpdateUtente()
  const { mutateAsync: remove } = useDeleteUtente()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()

  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.ruolo) count++
    return count
  }, [filters])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("users.titolo_lista")}
            breadcrumbs={[
              {
                label: t("users.utenti"),
                iconType: ManageAccounts,
              },
            ]}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              createModalActions.open()
            }}
          >
            {t("users.cta_aggiungi")}
          </Button>
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1, ruolo: "" })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Box display={"flex"} alignItems={"center"}>
                <Box sx={{ width: 200 }}>
                  <FormControl size="small" sx={{ width: "100%" }}>
                    <InputLabel id="demo-simple-select-label">{t("user.ruolo")}</InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={uiFilters.ruolo}
                      label={t("user.ruolo")}
                      onChange={(e) => setFilters({ ...filters, ruolo: e.target.value, page: 1 })}
                    >
                      <MenuItem value={""}>{t("general.tutti")}</MenuItem>
                      <MenuItem value={"amministratore"}>{t("user.enum_ruolo.amministratore")}</MenuItem>
                      <MenuItem value={"consultazione"}>{t("user.enum_ruolo.consultazione")}</MenuItem>
                      <MenuItem value={"operatore"}>{t("user.enum_ruolo.operatore")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<User>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("users.cta_aggiungi")}
        FormComponent={UtenteForm}
        save={(values: User) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />

      <DialogForm<User>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("user.cta_modifica")}
        item={editModalState.value}
        hasCancel
        FormComponent={UtenteForm}
        save={(values: User) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("user.esito_modifica_ok"))
          })
        }}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("user.cta_elimina")}
        subtitle={t("user.cta_subtitle_elimina") + " " + deleteModalState.value?.full_name + "?"}
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id).then(() => {
            deleteModalActions.close()
            showMessage(t("user.esito_elimina_ok"))
          })
        }}
      />
      <DialogConfirm
        open={reinviaEmailModalState.isOpen}
        handleClose={reinviaEmailModalActions.close}
        title={t("user.cta_reinvia_email")}
        subtitle={t("user.cta_subtitle_reinvia_email") + " " + reinviaEmailModalState.value?.full_name + "?"}
        textConfirm={t("general.reinvia")}
        bgConfirm="primary"
        onConfirm={() => {
          return fetchApi(`${API_URL}/utenti/${reinviaEmailModalState.value.id}/resend-activation-email/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tokens?.access}`,
            },
          }).then(() => {
            reinviaEmailModalActions.close()
            showMessage(t("user.email_inviata"))
          })
        }}
      />
      <DialogConfirm
        open={sbloccaModalState.isOpen}
        handleClose={sbloccaModalActions.close}
        title={t("user.cta_sblocca")}
        subtitle={t("user.cta_subtitle_sblocca") + " " + sbloccaModalState.value?.full_name + "?"}
        textConfirm={t("general.sblocca")}
        bgConfirm="primary"
        onConfirm={() => {
          return fetchApi(`${API_URL}/utenti/${sbloccaModalState.value.id}/unlock/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tokens?.access}`,
            },
          }).then(() => {
            sbloccaModalActions.close()
            showMessage(t("user.utente_sbloccato"))
            refetch()
          })
        }}
      />
    </Layout>
  )
}
